/* eslint-disable react/jsx-no-bind */
/* eslint-disable react/forbid-prop-types */
/* eslint-disable react/destructuring-assignment */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useForm, useFieldArray } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { quote } from '@monestry-dev/schema';
import { putOrDeleteQuotes } from '../../redux/reducers/quotes';
import { setMessage } from '../../redux/actions/message';
import { globalQuotesArrayView } from '../../redux/reducers/globalSelectors';
import { controlData, questions, calculateValue, getMissingFields } from './unlistedAddValuationControl';
import ButtonBar from './ButtonBar';
import getButtonsToShow from './getButtonsToShow';
import OptionButtons from '../../elements/OptionButtons';
import OptionButtonsMultiple from '../../elements/OptionButtonsMultiple';
import OptionArray from '../../elements/OptionArray';
import InputField from '../../elements/InputField';
import ToolTip from '../../elements/ToolTip';
import Button from '../../elements/Button';

dayjs.extend(utc);

export default function UnlistedAddValuationWizard(props) {
  const { t } = useTranslation('accountDetails', { keyPrefix: 'unlistedShares.wizard.valuation' });
  const { t: tMethods } = useTranslation('accountDetails', { keyPrefix: 'unlistedShares.wizard.valuation.methods' });
  const { t: tButtons } = useTranslation('accountDetails', { keyPrefix: 'buttons' });

  const selectQuotes = useSelector(globalQuotesArrayView);
  const baseCurrency = useSelector((state) => state.user.profile.settings.baseCurrency);
  // all quotes are manual in unlisted; quotes API returns all manual quotes "which change something",
  // i.e. which were the one actually entered by users (as opposed to those generated from a past quote for a requested date)
  // so we want to see only them (they are the ones with date === quoteDate)
  const existingQuotes = useMemo(() => (selectQuotes.filter((q) => q.assetId === props.account.id && q.date === q.quoteDate) || []).sort((a, b) => a.date - b.date), [selectQuotes, props.account.id]);

  const noteFieldNames = useMemo(() => ['stage', 'valuationMethods', 'percentOwned', ...questions.map((q) => q.id)], []);

  function parseNoteObject(note) {
    if (!note) return {};
    if (typeof note === 'string') {
      try {
        return JSON.parse(note);
      } catch (err) {
        console.error(err);
        return {};
      }
    }
    if (typeof note === 'object') return note;
    return {};
  }

  function quoteToFormState(quoteObject = {}) {
    const noteObject = parseNoteObject(quoteObject.note);
    const noteValues = noteFieldNames.reduce((acc, fieldName) => {
      acc[fieldName] = typeof noteObject[fieldName] === 'undefined' ? null : noteObject[fieldName];
      return acc;
    }, {});
    return {
      assetId: quoteObject.assetId || props.account.id,
      projectId: quoteObject.projectId || null,
      source: quoteObject.source || 'manual',
      date: quoteObject.date ? dayjs.utc(quoteObject.date).format('YYYY-MM-DD') : null,
      quote: quoteObject.quote ?? null,
      quoteBaseCurrency: quoteObject.quoteBaseCurrency ?? null,
      ...noteValues,
    };
  }

  const defaultQuotes = useMemo(() => existingQuotes.map((q) => quoteToFormState(q)).concat([quoteToFormState({ assetId: props.account.id })]), [existingQuotes, props.account.id]);

  // determines which quote from quotes array do we currently see (initialise on the last position, which is the empty object for a new quote)
  const [selectedQuoteIndex, setSelectedQuoteIdIndex] = useState(defaultQuotes.length ? defaultQuotes.length - 1 : 0); // initialise with the new quote
  // const [newQuoteDraft, setNewQuoteDraft] = useState({});
  // const unlistedQuoteIndex = useMemo(() => {
  //   if (!selectedQuoteIndex) return quotes.length - 1;
  //   const foundIndex = quotes.findIndex((q) => q.id === selectedQuoteIndex);
  //   return foundIndex >= 0 ? foundIndex : quotes.length - 1;
  // }, [selectedQuoteIndex, quotes]);

  const formObject = useForm({
    defaultValues: { quotes: defaultQuotes },
  });
  const { register, reset, resetField, watch, formState, setValue, handleSubmit, control, getFieldState } = formObject;
  const { replace } = useFieldArray({ control, name: 'quotes' });
  const watchAllQuotes = watch('quotes');
  const currentQuote = watchAllQuotes?.[selectedQuoteIndex] || {};
  const { stage, valuationMethods } = currentQuote;
  const quotePath = `quotes.${selectedQuoteIndex}`;
  const totalQuotes = (watchAllQuotes || defaultQuotes).length || 1;
  const fieldSetState = getFieldState(`quotes.${selectedQuoteIndex}`, formState); // get state of a single fieldset in field array

  const [displayedQuestions, setDisplayedQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    replace(defaultQuotes);
    reset({ quotes: defaultQuotes });
    setSelectedQuoteIdIndex(defaultQuotes.length ? defaultQuotes.length - 1 : 0);
  }, [defaultQuotes, replace, reset]);

  // -----------------------------
  // HANDLE PREV / NEXT BUTTONS
  // -----------------------------

  // const isNewQuote = unlistedQuoteIndex === quotes.length - 1 || !quotes[unlistedQuoteIndex]?.id;

  // helper: reset the form and updated the index in one function
  function resetAndSetIndex(newIndex) {
    const boundedIndex = Math.min(Math.max(newIndex, 0), totalQuotes - 1);
    setSelectedQuoteIdIndex(boundedIndex);
  }

  function handlePrevNext(direction) {
    const newIndex = direction === 'next' ? selectedQuoteIndex + 1 : selectedQuoteIndex - 1;

    // Ensure the new index is within bounds
    resetAndSetIndex(newIndex);
  }

  // -----------------------------
  // HANDLE SUBMIT
  // -----------------------------

  // submit button is enabled only when the form is VALID and DIRTY
  async function onSubmit(formValues) {
    const inputQuote = formValues?.quotes?.[selectedQuoteIndex] || {};
    setLoading(true);

    // remember to move everything exept quote and date under note and stringify it
    let transformedQuote;
    try {
      transformedQuote = quote.cast(
        {
          // apply quotes in proportion to the percent owned indicated by user; if there is no percent owned, assume 100% (i.e. intial quote for the account)
          quote: Number(inputQuote.quote) * (Number(inputQuote.percentOwned) / 100 || 1),
          assetId: props.account.id,
          quoteDate: dayjs.utc(inputQuote.date).valueOf(),
          date: dayjs.utc(inputQuote.date).valueOf(),
          quoteBaseCurrency: Number(inputQuote.quoteBaseCurrency) * (Number(inputQuote.percentOwned) / 100 || 1),
          currency: props.account.currency,
          source: 'manual',
          projectId: null,
        },
        { stripUnknown: true },
      );
    } catch (err) {
      console.error(err);
      dispatch(setMessage('validationError'));
      setLoading(false);
      return;
    }

    // get every property except date/quote/quoteBaseCurrency and put it in note as stringified JSON
    try {
      const noteExcluded = new Set(['date', 'quote', 'quoteBaseCurrency', 'quoteId']);
      const note = JSON.stringify(
        Object.entries(inputQuote)
          .filter(([key]) => !noteExcluded.has(key))
          .reduce((acc, [key, value]) => {
            acc[key] = value;
            return acc;
          }, {}),
      );

      await dispatch(
        putOrDeleteQuotes({
          arrayOfQuotes: [
            {
              ...transformedQuote,
              note,
            },
          ],
          accountId: props.account.id,
        }),
      );
    } catch (err) {
      console.error(err);
      dispatch(setMessage('validationError'));
    }
    setLoading(false);
    props.handleClick('table');
  }

  // -----------------------------
  // HANDLE DELETE
  // -----------------------------

  function handleResetNewQuote() {
    const emptyQuote = quoteToFormState({ assetId: props.account.id });
    resetField(quotePath, { defaultValue: emptyQuote });
    setValue(quotePath, emptyQuote, { shouldDirty: false });
    setDisplayedQuestions([]);
  }

  function handleDelete() {
    setDeleting(true);
    // if this is a "previous" quote (i.e. not the last item of the array), ask confirmation and send a delete to backend
    // if this is a "new" quote and it is dirty, reset the form (ask confirmation first)
    // if this is a "new" quote and it is NOT dirty, do nothing (disabled delete button should not allow this to be executed)
    const thisIsTheLastQuote = selectedQuoteIndex === totalQuotes - 1;
    // first check if the form is dirty, and if it is, display warning
    if ((thisIsTheLastQuote && formState.isDirty) || !thisIsTheLastQuote) {
      // before navigating away from the form, display array
      const event = new CustomEvent('setAlert', {
        detail: {
          id: 'youAreAboutToDeleteValuation',
          caller: 'UnlistedAddValuationWizard',
          callbackOk: async () => {
            try {
              // if this is an existing quote (i.e. not the last item of the array), send a delete to backend
              if (!thisIsTheLastQuote) {
                await dispatch(putOrDeleteQuotes({ arrayOfQuotes: [{ ...existingQuotes[selectedQuoteIndex], importFlag: 'delete' }], accountId: props.account.id }));
                props.handleClick('table');
              } else {
                // if this is a "new" quote, reset the form
                handleResetNewQuote();
              }
              setDeleting(false);
            } catch (err) {
              console.error(err);
              setDeleting(false);
            }
          },
        },
      });
      window.dispatchEvent(event);
    }
    setDeleting(false);
  }

  // -----------------------------
  // HANDLE FORM MECHANICS
  // -----------------------------

  const stageButtons = [
    { id: 'idea', name: t('stage.idea.label') },
    { id: 'prototype', name: t('stage.prototype.label') },
    { id: 'mvp', name: t('stage.mvp.label') },
    { id: 'expansion', name: t('stage.expansion.label') },
    { id: 'market', name: t('stage.market.label') },
  ];

  const allMethods = [
    { id: 'berkus', name: t('methods.berkus.label'), tooltip: t('methods.berkus.tooltip') },
    { id: 'scorecard', name: t('methods.scorecard.label'), tooltip: t('methods.scorecard.tooltip') },
    { id: 'riskFactor', name: t('methods.riskFactor.label'), tooltip: t('methods.riskFactor.tooltip') },
    { id: 'valuationByStage', name: t('methods.valuationByStage.label'), tooltip: t('methods.valuationByStage.tooltip') },
    { id: 'replacementCost', name: t('methods.replacementCost.label'), tooltip: t('methods.replacementCost.tooltip') },
    { id: 'bookValue', name: t('methods.bookValue.label'), tooltip: t('methods.bookValue.tooltip') },
    { id: 'fiveTimesYourRise', name: t('methods.fiveTimesYourRise.label'), tooltip: t('methods.fiveTimesYourRise.tooltip') },
    { id: 'dcf', name: t('methods.dcf.label'), tooltip: t('methods.dcf.tooltip') },
  ];

  // when user selects a stage, show only the methods that apply to that stage (part of the valuation business object, so update it there)
  useEffect(() => {
    if (stage) {
      const methodsPerStage = controlData.filter((method) => method.appliesTo.includes(stage));
      const allMethodsToShow = allMethods.filter((method) => methodsPerStage.map((m) => m.id).includes(method.id));
      if (!valuationMethods || valuationMethods.length === 0) {
        setValue(
          `${quotePath}.valuationMethods`,
          allMethodsToShow.map((method) => ({ id: method.id, selected: true })),
          { shouldDirty: false },
        );
      }
    }
  }, [stage, selectedQuoteIndex, valuationMethods, setValue, quotePath]);

  // generate a list of questions to be displayed in reaction to changes of method selection
  useEffect(() => {
    const questionIds = new Set();
    if (valuationMethods) {
      // get a list of unique questionIds for all currently selected methods using a Set
      valuationMethods
        .filter((method) => method.selected)
        .forEach((selectedMethod) => {
          const methodObject = controlData.find((method) => method.id === selectedMethod.id);
          methodObject.questions.forEach((question) => questionIds.add(question));
        });

      // update the component state (question components will self-register in form state)
      setDisplayedQuestions(Array.from(questionIds));
    } else {
      // if there are no valuationMethods (like for example when we go back from previous to new quote), show no questions
      setDisplayedQuestions([]);
    }
  }, [stage, valuationMethods, selectedQuoteIndex]);

  // -----------------------------
  // RENDER COMPONENT
  // -----------------------------

  return (
    <div className="w-full h-full">
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <ButtonBar {...props} buttonsToShow={getButtonsToShow('unlistedShares', 'wizard', 'quotes')} handlePrevNext={handlePrevNext} />
      <form className="w-full h-full grid lg:grid-cols-2 xl:grid-cols-3 py-0 px-2 overflow-y-auto" onSubmit={handleSubmit(onSubmit)}>
        {/* COL 1: STAGES + METHODS */}

        <section id="unlisted-add-valuation-col-1" className="space-y-8">
          <div>
            <h3 className="text-lg font-bold">{t('title')}</h3>
            <p className="text-sm mt-2">{t('description')}</p>
          </div>
          <OptionButtons
            key={`stage-${quotePath}`}
            control={control}
            id={`${quotePath}.stage`}
            label={t('stage.label')}
            tooltip={t('stage.tooltip')}
            forceFlex
            fullWidth
            overrideOptions={stageButtons}
          />
          {stage && (
            <OptionButtonsMultiple
              key={`valuationMethods-${quotePath}`}
              control={control}
              id={`${quotePath}.valuationMethods`}
              label={t('valuationMethod.label')}
              tooltip={t('valuationMethod.tooltip')}
              tPrefix="accountDetails.unlistedShares.wizard.valuation.methods"
            />
          )}
        </section>

        {/* COL 2: QUESTIONS */}

        <section className="space-y-8 overflow-y-auto mb-8" id="unlisted-add-valuation-col-2">
          {displayedQuestions.map((question) => {
            const questionObject = questions.find((q) => q.id === question);
            if (questionObject.type === 'score') {
              return (
                <OptionButtons
                  key={`${question}-${selectedQuoteIndex}`}
                  control={control}
                  id={`${quotePath}.${question}`}
                  label={t(`${question}.label`)}
                  tooltip={t(`${question}.tooltip`)}
                  tPrefix="accountDetails.unlistedShares.wizard.valuation"
                />
              );
            }
            if (questionObject.type === 'array') {
              return (
                <OptionArray
                  key={`${question}-${selectedQuoteIndex}`}
                  formObject={formObject}
                  control={control}
                  currency={props.account.currency}
                  name={`${quotePath}.${question}`}
                  label={t(`${question}.label`)}
                  tooltip={t(`${question}.tooltip`)}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...(questionObject.id === 'cashFlowArray' && { namePlaceholder: t('cashFlowArray.namePlaceholder') })}
                />
              );
            }
            if (questionObject.type === 'number') {
              return (
                <InputField
                  key={`${question}-${selectedQuoteIndex}`}
                  id={`${quotePath}.${question}`}
                  type="text"
                  register={register}
                  errors={formState.errors}
                  // rate fields have a placeholder which looks like a percentage; the rest are amounts, so they have a placeholder that looks like an amount
                  placeholder={['discountRate', 'longTermGrowthRate'].includes(questionObject.id) ? '8' : '100000'}
                  label={t(`${question}.label`)}
                  toolTip={t(`${question}.tooltip`)}
                  category="accountDetails.unlistedShares.wizard.valuation"
                  formatting="w-1/2"
                  optional={false}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...(questionObject.id === 'longTermGrowthRate' && {
                    validationRules: { validate: (value) => parseFloat(value) <= currentQuote.discountRate || 'Value must be greater than discount rate.' },
                  })}
                />
              );
            }
            return null;
          })}
        </section>

        {/* COL 3: RESULTS */}
        <section className="space-y-8 p-4" id="unlisted-add-valuation-col-3">
          <div className="border border-gray-100 bg-white round-lg p-4">
            <h2 className="text-sm font-bold uppercase">{t('finalQuote.label')}</h2>
            <p className="mt-1 mb-2 text-sm">{t('finalQuote.description')}</p>
            <InputField
              key={`date-${quotePath}`}
              id={`${quotePath}.date`}
              type="date"
              register={register}
              errors={formState.errors}
              placeholder={dayjs().format('YYYY-MM-DD')}
              disableLabel
              toolTip={t('finalQuote.tooltip')}
              category="accountDetails.unlistedShares.wizard.valuation"
              formatting="w-1/2"
              optional={false}
              validationRules={{ required: { value: true, message: t('finalQuote.validation.required') } }}
            />
            <InputField
              key={`quote-${quotePath}`}
              id={`${quotePath}.quote`}
              type="text"
              register={register}
              errors={formState.errors}
              placeholder="100000"
              disableLabel
              toolTip={t('finalQuote.tooltip')}
              category="accountDetails.unlistedShares.wizard.valuation"
              formatting="w-1/2"
              optional={false}
              validationRules={{ required: { value: true, message: t('finalQuote.validation.required') } }}
              currency={props.account.currency}
            />
            {props.account.currency !== baseCurrency && (
              <InputField
                key={`quoteBaseCurrency-${quotePath}`}
                id={`${quotePath}.quoteBaseCurrency`}
                type="text"
                register={register}
                errors={formState.errors}
                placeholder="100000"
                disableLabel
                toolTip={t('finalQuote.tooltip')}
                category="accountDetails.unlistedShares.wizard.valuation"
                formatting="w-1/2"
                optional={false}
                validationRules={{ required: { value: true, message: t('finalQuote.validation.required') } }}
                currency={baseCurrency}
              />
            )}
            <InputField
              key={`percentOwned-${quotePath}`}
              id={`${quotePath}.percentOwned`}
              type="text"
              label={t('percentOwned.label')}
              register={register}
              errors={formState.errors}
              placeholder="100"
              toolTip={t('percentOwned.tooltip')}
              category="accountDetails.unlistedShares.wizard.valuation"
              formatting="w-1/2"
              optional={false}
              validationRules={{ required: { value: true, message: t('finalQuote.validation.required') } }}
              currency="%"
            />
            <div className="flex mt-4 space-x-2">
              <Button
                text={tButtons('previous.label')}
                enabled={selectedQuoteIndex !== 0 && !loading}
                onClick={() => handlePrevNext('previous')}
                toolTip={tButtons('previous.tooltip')}
                id="button-previous"
              />
              <Button type="submit" text={t('finalQuote.save')} withAccent className="mt-2" enabled={!fieldSetState.invalid && fieldSetState.isDirty && !deleting} size="xl" spinnerOn={loading} />
              {selectedQuoteIndex === totalQuotes - 1 ? (
                <Button type="button" text={t('finalQuote.resetThisQuote')} className="mt-2" size="xl" onClick={handleResetNewQuote} enabled={!loading} />
              ) : (
                <Button type="button" text={t('finalQuote.deleteThisQuote')} className="mt-2" size="xl" onClick={handleDelete} enabled={!loading} spinnerOn={deleting} />
              )}
              <Button
                text={tButtons('next.label')}
                enabled={selectedQuoteIndex < totalQuotes - 1 && !loading}
                onClick={() => handlePrevNext('next')}
                toolTip={tButtons('next.tooltip')}
                id="button-next"
              />
            </div>
          </div>
          {(valuationMethods || [])
            .filter((o) => o.selected)
            .map((methodObject) => {
              const value = calculateValue(methodObject.id, currentQuote);
              return (
                <div key={methodObject.id}>
                  <h2 className="text-sm font-bold uppercase">{tMethods(`${methodObject.id}.label`)}</h2>
                  {value ? (
                    <p className="mt-1 text-xl font-bold">{(value || '').toLocaleString('de', { style: 'currency', currency: props.account.currency, maximumFractionDigits: 0 })}</p>
                  ) : (
                    <div className="flex space-x-0.5 items-baseline">
                      <p className="mt-1 text-sm italic text-gray-400">{t('insufficientData')}</p>
                      <ToolTip
                        formatting="-mb-1"
                        info={t('insufficientDataTooltip', {
                          fields: getMissingFields(methodObject.id, currentQuote)
                            .map((x) => t(`${x}.label`))
                            .join(', '),
                        })}
                      />
                    </div>
                  )}
                </div>
              );
            })}
        </section>
      </form>
    </div>
  );
}
UnlistedAddValuationWizard.propTypes = {
  account: PropTypes.shape({
    currency: PropTypes.string,
    id: PropTypes.string,
  }).isRequired,
  data: PropTypes.array.isRequired,
  unlistedQuoteIndex: PropTypes.number.isRequired,
  handleClick: PropTypes.func.isRequired,
};
